import { createFileRoute } from "@tanstack/react-router";
import { useAgent, useAgentSims } from "@/hooks/use-agents";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  IconArrowLeft,
  IconDeviceSim,
  IconActivity,
  IconSignal2g,
  IconDeviceMobile,
  IconRefresh,
} from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/agents/$id/sims/")({
  component: AgentSimsPage,
});

function AgentSimsPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <AgentSimsContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AgentSimsContent() {
  const { id } = Route.useParams();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: agent, isLoading: agentLoading } = useAgent(id);
  const { data: sims, isLoading: simsLoading, refetch } = useAgentSims(id, {
    status: statusFilter === "all" ? undefined : (statusFilter as any),
  });

  if (agentLoading || simsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading agent SIMs...</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <IconDeviceSim className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive font-medium">Agent not found</p>
        </div>
      </div>
    );
  }

  const simsData = (sims as any)?.sims || [];
  const stats = simsData ? {
    total: simsData.length,
    active: simsData.filter((sim: any) => sim.status === 'active').length,
    pending: simsData.filter((sim: any) => sim.status === 'pending').length,
    suspended: simsData.filter((sim: any) => sim.status === 'suspended').length,
  } : { total: 0, active: 0, pending: 0, suspended: 0 };

  const getOperatorColor = (operator: string) => {
    switch (operator?.toLowerCase()) {
      case "bkash":
        return "text-pink-600 bg-pink-50";
      case "nagad":
        return "text-orange-600 bg-orange-50";
      case "rocket":
        return "text-blue-600 bg-blue-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "pending":
        return "secondary";
      case "suspended":
        return "destructive";
      case "inactive":
        return "outline";
      default:
        return "outline";
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/agents">
            <Button variant="ghost" size="sm">
              <IconArrowLeft className="h-4 w-4 mr-2" />
              Back to Agents
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {(agent as any).agent_name}'s SIMs
            </h1>
            <p className="text-muted-foreground">
              Manage SIM cards assigned to this agent
            </p>
          </div>
        </div>
        <Button onClick={() => refetch()}>
          <IconRefresh className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total SIMs</CardTitle>
            <IconDeviceSim className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Registered SIM cards</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <IconActivity className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <p className="text-xs text-muted-foreground">Ready for transactions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <IconActivity className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
            <p className="text-xs text-muted-foreground">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Suspended</CardTitle>
            <IconActivity className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.suspended}</div>
            <p className="text-xs text-muted-foreground">Temporarily suspended</p>
          </CardContent>
        </Card>
      </div>

      {/* SIMs Table */}
      <Card>
        <CardHeader>
          <CardTitle>SIM Cards</CardTitle>
          <CardDescription>
            All SIM cards registered under this agent
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Filter by status:</span>
              <Button
                variant={statusFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("all")}
              >
                All
              </Button>
              <Button
                variant={statusFilter === "active" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("active")}
              >
                Active
              </Button>
              <Button
                variant={statusFilter === "pending" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("pending")}
              >
                Pending
              </Button>
              <Button
                variant={statusFilter === "suspended" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("suspended")}
              >
                Suspended
              </Button>
            </div>

            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Operator</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Device Status</TableHead>
                    <TableHead>Transactions</TableHead>
                    <TableHead>Registered</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!simsData || simsData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        No SIMs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    simsData.map((sim: any) => (
                      <TableRow key={sim.id}>
                        <TableCell>
                          <div className="font-mono font-medium">
                            {sim.phoneNumber}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={getOperatorColor(sim.operator)}
                          >
                            {sim.operator}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(sim.status)}>
                            <span className="capitalize">{sim.status}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">৳{sim.balance || 0}</div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {sim.isOnline ? (
                              <div className="flex items-center gap-1 text-green-600">
                                <IconSignal2g className="w-4 h-4" />
                                <span className="text-sm">Online</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-gray-500">
                                <IconDeviceMobile className="w-4 h-4" />
                                <span className="text-sm">Offline</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{sim.stats?.totalTransactions || 0} total</div>
                            <div className="text-muted-foreground">
                              ৳{sim.stats?.totalVolume || 0} volume
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {sim.createdAt
                              ? formatDistanceToNow(new Date(sim.createdAt), {
                                  addSuffix: true,
                                })
                              : "Unknown"}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}